import { useEffect, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { History, Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export interface Conversation {
  id: string;
  title: string;
  updated_at: string;
}

interface Props {
  activeId: string | null;
  onSelect: (id: string | null) => void;
  refreshToken: number;
}

export function StewHistoryDrawer({ activeId, onSelect, refreshToken }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Conversation | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("stew_conversations")
      .select("id, title, updated_at")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false })
      .limit(100);
    if (error) {
      console.error("StewHistoryDrawer load failed:", error);
      toast({
        title: "Your history didn't load",
        description:
          "Give it a moment and try opening History again. If it keeps happening, open a ticket.",
        variant: "destructive",
      });
    } else {
      setItems((data || []) as Conversation[]);
    }
    setLoading(false);
  }, [user, toast]);

  useEffect(() => {
    if (open) void load();
  }, [open, load, refreshToken]);

  // Also reload silently on refresh token bumps so an active-side title update
  // is reflected the next time the drawer opens.
  useEffect(() => {
    if (user) void load();
  }, [refreshToken, user, load]);

  const startNew = () => {
    onSelect(null);
    setOpen(false);
  };

  const remove = async (id: string) => {
    const prev = items;
    setItems((cur) => cur.filter((c) => c.id !== id));
    const { error } = await supabase.from("stew_conversations").delete().eq("id", id);
    if (error) {
      console.error("StewHistoryDrawer delete failed:", error);
      setItems(prev);
      toast({
        title: "That conversation didn't delete",
        description:
          "Try once more. If it keeps happening, open a ticket.",
        variant: "destructive",
      });
      return;
    }
    if (activeId === id) onSelect(null);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <History className="h-4 w-4" />
            History
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-[92vw] max-w-[22rem] sm:max-w-sm p-0 flex flex-col h-[100dvh]"
        >
          <StewHistoryDrawerContent
            activeId={activeId}
            items={items}
            loading={loading}
            onDelete={setConfirmDelete}
            onSelect={(id) => {
              onSelect(id);
              setOpen(false);
            }}
            onStartNew={startNew}
          />
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const c = confirmDelete;
                setConfirmDelete(null);
                if (c) void remove(c.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function StewHistoryDrawerContent({
  activeId,
  items,
  loading,
  onDelete,
  onSelect,
  onStartNew,
}: {
  activeId: string | null;
  items: Conversation[];
  loading: boolean;
  onDelete: (conversation: Conversation) => void;
  onSelect: (id: string) => void;
  onStartNew: () => void;
}) {
  return (
    <>
      <SheetHeader className="px-4 py-3 border-b shrink-0">
        <SheetTitle className="text-base">Your conversations</SheetTitle>
      </SheetHeader>
      <div className="p-3 border-b shrink-0">
        <Button onClick={onStartNew} className="w-full gap-2" size="sm">
          <Plus className="h-4 w-4" />
          New conversation
        </Button>
      </div>
      <div
        className="flex-1 min-h-0 w-full max-w-full overflow-y-auto overflow-x-hidden"
        // Reserve gutter space so classic (space-taking) scrollbars don't
        // clip the trailing delete icons in list rows. Overlay-scrollbar
        // browsers ignore this and behave as before.
        style={{ scrollbarGutter: "stable" }}
      >
        <div className="w-full max-w-full box-border overflow-hidden p-2 pr-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground px-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading
            </div>
          )}
          {!loading && items.length === 0 && (
            <p className="text-sm text-muted-foreground px-2 py-6 text-center">
              No past conversations yet.
            </p>
          )}
          <ul className="w-full max-w-full space-y-1">
            {items.map((c) => (
              <li key={c.id} className="group w-full max-w-full">
                <div
                  className={cn(
                    "flex w-full max-w-full box-border items-start gap-2 rounded-md pl-2 pr-2 py-2 hover:bg-accent",
                    activeId === c.id && "bg-accent",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(c.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="text-sm font-medium truncate">
                      {c.title || "Conversation"}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                      {formatDistanceToNow(new Date(c.updated_at), {
                        addSuffix: true,
                      })}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(c)}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label="Delete conversation"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
